/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, of } from 'rxjs'

import {
  ContentRecord,
  Environment,
  GeneratedContentDetails,
} from '@cyborg-monorepo/hunter/common'
import { generateContent } from '@cyborg-monorepo/hunter/matchboi'

import { VerityEnvironmentService } from '../../../../environments/verity-environment.service'

export interface EnvironmentFilters {
  environments?: Array<string>
  tools?: Array<string>
  categories?: Array<string>
}

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  private httpClient = inject(HttpClient)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  /**
   * Get generated content for a given content record
   */
  public getContent(
    content: Partial<ContentRecord>,
    isCustomPackage = false,
  ): Observable<GeneratedContentDetails> {
    return of(
      this.generateContentDetails(content, content.content, isCustomPackage),
    )
  }

  /**
   * Get all additional generated content for a given content record
   */
  public getAdditionalContent(
    content: Partial<ContentRecord>,
    isCustomPackage = false,
  ): Observable<GeneratedContentDetails[]> {
    return of(
      content.additional_queries.map((query) =>
        this.generateContentDetails(content, query, isCustomPackage),
      ),
    )
  }

  private generateContentDetails(
    content: Partial<ContentRecord>,
    targetContentTemplate: string,
    isCustomPackage = false,
  ): GeneratedContentDetails {
    if (isCustomPackage) {
      return {
        tool: content.tool,
        content: [
          {
            query: targetContentTemplate,
            category: content.deployment_requirements.categories[0],
          },
        ],
      }
    }

    const generatedContents = generateContent(
      targetContentTemplate,
      content.tool,
      content.deployment_requirements.categories,
    )

    return {
      tool: content.tool,
      content: generatedContents.map((generatedContent) => {
        return {
          query: generatedContent.query,
          category: generatedContent.category,
        }
      }),
    }
  }

  public getHuntPackageContent(
    uuid: string,
    envFilters?: EnvironmentFilters,
  ): Observable<Environment[]> {
    let params = new HttpParams()

    if (envFilters?.environments)
      envFilters.environments.forEach((env) => {
        params = params.append('environments', env)
      })

    if (envFilters?.tools)
      envFilters.tools.forEach((tool) => {
        params = params.append('tools', tool)
      })

    if (envFilters?.categories)
      params = params.append('categories', envFilters?.categories?.join(', '))

    return this.httpClient.get<any>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/${uuid}/content`,
      {
        params: params,
      },
    )
  }

  public generateCustomContent(
    huntTemplateUUID: string,
    customHuntPackageUUID: string,
    envFilters?: EnvironmentFilters,
  ): Observable<Environment[]> {
    let params = new HttpParams()

    if (envFilters?.environments)
      envFilters.environments.forEach((env) => {
        params = params.append('environments', env)
      })

    if (envFilters?.tools)
      envFilters.tools.forEach((tool) => {
        params = params.append('tools', tool)
      })

    if (envFilters?.categories)
      params = params.append('categories', envFilters?.categories?.join(', '))

    params = params.append('huntTemplateUUID', huntTemplateUUID)
    params = params.append('customHuntPackageUUID', customHuntPackageUUID)

    return this.httpClient.get<any>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-custom-hunt-package/generate-content/`,
      {
        params: params,
      },
    )
  }
}
